import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, Camera, Image as ImgIcon, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { extractFromPhoto } from "@/lib/api";

const Dashboard = () => {
  const navigate = useNavigate();
  const [dragOver, setDragOver] = useState(false);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const handleFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (files.length === 0) return;

    for (const f of files) {
      const isImage = f.type.startsWith("image/");
      const isPdf =
        f.type === "application/pdf" ||
        f.name.toLowerCase().endsWith(".pdf");
      if (!isImage && !isPdf) {
        toast.error(`${f.name}: unsupported format`);
        return;
      }
    }

    setLoading(true);
    try {
      toast.info(
        files.length === 1
          ? "Analyzing with AI vision..."
          : `Analyzing ${files.length} pages with AI vision...`
      );
      const extracted = await extractFromPhoto(files);

      if (!extracted.rooms || extracted.rooms.length === 0) {
        toast.warning("No rooms detected. You can still add them manually.");
      } else {
        toast.success(
          `Found ${extracted.rooms.length} room(s) across ${
            extracted.photos?.length || files.length
          } page(s)`
        );
      }

      sessionStorage.setItem("pending_quote", JSON.stringify(extracted));
      navigate("/review");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Extraction failed");
    } finally {
      setLoading(false);
    }
  };

  const startBlank = () => {
    sessionStorage.setItem(
      "pending_quote",
      JSON.stringify({
        client_name: "",
        color: "",
        area: "",
        rooms: [],
        photos: [],
      })
    );
    navigate("/review");
  };

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      {/* Header */}
      <div className="mb-10">
        <div className="label-xs mb-3">Module 01 / Intake</div>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[0.95]">
          UPLOAD SKETCH.
          <br />
          <span className="text-[#005bea]">EXTRACT ROOMS.</span>
          <br />
          PRICE IN SECONDS.
        </h1>
        <p className="mt-6 text-sm text-zinc-600 max-w-xl leading-relaxed">
          Snap a photo (or several) of your hand-drawn measurement sheets, or
          drop a multi-page PDF. The system reads room names and dimensions
          (mm), then applies your per-m² rate for each mosquito screen type.
        </p>
      </div>

      {/* Dropzone */}
      <div
        data-testid="upload-dropzone"
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          handleFiles(e.dataTransfer.files);
        }}
        className={`relative bracket blueprint-grid border border-zinc-900 min-h-[360px] flex flex-col items-center justify-center p-10 transition-colors ${
          dragOver ? "bg-blue-50" : "bg-white"
        }`}
      >
        {loading ? (
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="animate-spin" size={48} />
            <div className="label-xs">Reading sketch...</div>
            <div className="font-mono text-xs text-zinc-500">
              Gemini vision extracting room data
            </div>
          </div>
        ) : (
          <>
            <div className="w-16 h-16 border border-zinc-900 flex items-center justify-center mb-6">
              <Upload size={28} />
            </div>
            <div className="font-display text-2xl sm:text-3xl font-black tracking-tight text-center">
              DROP PHOTO HERE
            </div>
            <div className="label-xs mt-3 mb-8">
              JPG · PNG · WEBP · PDF · MULTIPLE OK · MAX 25MB EACH
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                data-testid="upload-file-btn"
                onClick={() => fileInputRef.current?.click()}
                className="btn-sharp"
              >
                <ImgIcon size={14} /> Browse files
              </button>
              <button
                data-testid="upload-camera-btn"
                onClick={() => cameraInputRef.current?.click()}
                className="btn-sharp secondary"
              >
                <Camera size={14} /> Use camera
              </button>
              <button
                data-testid="start-blank-btn"
                onClick={startBlank}
                className="btn-sharp secondary"
              >
                Enter manually
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,application/pdf,.pdf"
              multiple
              hidden
              onChange={(e) => handleFiles(e.target.files)}
              data-testid="file-input"
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              onChange={(e) => handleFiles(e.target.files)}
              data-testid="camera-input"
            />
          </>
        )}
      </div>

      {/* How it works strip */}
      <div className="grid grid-cols-1 md:grid-cols-3 mt-10 border border-zinc-200">
        {[
          {
            n: "01",
            t: "CAPTURE",
            d: "Photograph the hand sketch on site, dimensions in mm.",
          },
          {
            n: "02",
            t: "EXTRACT",
            d: "AI reads room names & measurements, you review & edit.",
          },
          {
            n: "03",
            t: "PRICE",
            d: "Assign screen type per room. Price calculated from your rates.",
          },
        ].map((s, i) => (
          <div
            key={s.n}
            className={`p-6 ${i < 2 ? "md:border-r border-zinc-200" : ""} ${
              i > 0 ? "border-t md:border-t-0 border-zinc-200" : ""
            }`}
          >
            <div className="font-mono text-xs text-[#005bea] font-bold">
              {s.n}
            </div>
            <div className="font-display text-xl font-black mt-2 tracking-tight">
              {s.t}
            </div>
            <div className="font-mono text-xs text-zinc-600 mt-3 leading-relaxed">
              {s.d}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Dashboard;
